/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @angular-eslint/no-empty-lifecycle-method */

import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';

import {
  DialogRef,
  ButtonComponent,
  IconComponent,
  FileUploadComponent,
  UploadedFile,
  AlertComponent,
  CheckboxComponent,
} from 'nebula-ui-kit';

import { TranslatePipe, TranslationService } from '@nebula-shared/data-access';

import { IMPORT_DIALOG_DATA, ImportDialogData } from './import-dialog-data.token';
import { ImportResponse, ImportResult } from './importar-general.model';
import { ImportDialogModalService } from '../../data-access/import-dialog-modal/import-dialog-modal.service';
import { ImportDialogDataService } from './import-dialog-data.service';

@Component({
  selector: 'app-importar-general-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    FileUploadComponent,
    AlertComponent,
    CheckboxComponent,
    TranslatePipe,
  ],
  templateUrl: './importar-general-dialog.component.html',
  styleUrls: ['./importar-general-dialog.component.scss'],
})
export class ImportarGeneralDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(DialogRef, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dependenciaService = inject(ImportDialogModalService);
  private readonly dialogData = inject<ImportDialogData>(IMPORT_DIALOG_DATA, { optional: true });
  translationService = inject(TranslationService);
  private alertaConfirmacion = signal(false);
  private archivoPendienteImportar: File | null = null;
  importando = signal(false);
  importacionCompletada = signal(false);

  archivoSeleccionado = signal<UploadedFile | null>(null);
  archivoOriginal: File | null = null;
  nombreArchivo = signal('');
  tieneEncabezado = signal(true);
  codificacionDetectada = signal<string>('Desconocida');
  bytesIniciales = signal<string>('');
  tieneBOM = signal<boolean>(false);
  codificacionValida = signal<boolean>(true);
  mostrarErrorCodificacion = signal<boolean>(false);

  importResponse = signal<ImportResponse | null>(null);

  mostrarAlerta = signal(false);
  alertaTipo = signal<'success' | 'error' | 'warning' | 'info'>('info');
  alertaTitulo = signal('');
  alertaMensaje = signal('');

  hayErrorCodificacion = signal(false);

  readonly summary = computed(() => this.importResponse()?.summary ?? null);
  readonly errors = computed(() => this.importResponse()?.errors ?? []);
  readonly hayErrores = computed(() => this.errors().length > 0);
  readonly todoCorrecto = computed(() => this.importacionCompletada() && !this.hayErrores());
  private readonly importDialogDataService = inject(ImportDialogDataService);

  puedeImportar = computed(() => {
    const tieneArchivo = this.archivoSeleccionado() !== null;
    const noImportando = !this.importando();

    if (!tieneArchivo || !this.archivoOriginal) return false;

    const extension = this.archivoOriginal.name?.split('.').pop()?.toLowerCase() || '';

    if (extension === 'xlsx' || extension === 'xls') {
      return tieneArchivo && noImportando;
    }

    const noErrorCodificacion = !this.hayErrorCodificacion();
    const codificacionCorrecta = this.codificacionValida();
    return tieneArchivo && noImportando && noErrorCodificacion && codificacionCorrecta;
  });

  private get endpoint(): string {
    if (this.dialogData?.endpoint) {
      return this.dialogData.endpoint;
    }
    const fallbackEndpoint = '/nebula-accounting-core/api/v1/dependencia/import';
    return fallbackEndpoint;
  }

  private get fieldName(): string {
    return this.dialogData?.fieldName || 'archivo';
  }

  private get dialogTitle(): string {
    return this.dialogData?.title || 'Importar Dependencias';
  }

  constructor() {
    this.importDialogDataService.data$.subscribe((data) => {
      if (data) {
        (this as any).dialogData = data;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    // to do
  }

  async onFilesSelected(files: UploadedFile[]): Promise<void> {
    if (files.length === 0) return;

    if (files.length > 1) {
      this.mostrarAlertaError('Solo se puede seleccionar un archivo a la vez');
      return;
    }

    const file = files[0];
    this.inicializarArchivo(file);

    if (this.archivoOriginal) {
      await this.validarArchivoPorExtension();
      this.mostrarInformacionFormato();
    }

    this.cdr.markForCheck();
  }

  private inicializarArchivo(file: UploadedFile): void {
    this.archivoSeleccionado.set(file);
    this.archivoOriginal = file.file || (file as any).raw || (file as unknown as File);

    if (!this.archivoOriginal) {
      this.archivoOriginal = file as unknown as File;
    }

    this.nombreArchivo.set(file.name || (this.archivoOriginal as any)?.name || '');
    this.importResponse.set(null);
    this.importacionCompletada.set(false);
    this.cerrarAlerta();
    this.alertaConfirmacion.set(false);

    this.hayErrorCodificacion.set(false);
    this.codificacionDetectada.set('Desconocida');
    this.bytesIniciales.set('');
    this.tieneBOM.set(false);
    this.codificacionValida.set(true);
    this.mostrarErrorCodificacion.set(false);
  }

  private async validarArchivoPorExtension(): Promise<void> {
    if (!this.archivoOriginal) return;

    const extension = this.archivoOriginal.name?.split('.').pop()?.toLowerCase() || '';

    if (extension === 'xlsx' || extension === 'xls') {
      this.validarExcel();
    } else if (extension === 'csv' || extension === 'txt') {
      await this.validarCSV();
    } else {
      this.validarOtroFormato(extension);
    }
  }

  private validarExcel(): void {
    this.codificacionValida.set(true);
    this.hayErrorCodificacion.set(false);
    this.mostrarErrorCodificacion.set(false);
  }
  private async validarCSV(): Promise<void> {
    if (!this.archivoOriginal) return;

    await this.detectarCodificacion(this.archivoOriginal);

    const esUTF8SinBOM = this.codificacionDetectada() === 'UTF-8 (sin BOM)';
    const esASCII = this.codificacionDetectada() === 'ASCII (compatible UTF-8)';
    const tieneBOM = this.tieneBOM();

    if (tieneBOM) {
      this.manejarErrorBOM();
    } else if (esASCII) {
      this.manejarErrorASCII();
    } else if (!esUTF8SinBOM) {
      this.manejarErrorCodificacionNoSoportada();
    } else {
      this.manejarCSVValido();
    }
  }

  private manejarErrorASCII(): void {
    this.codificacionValida.set(false);
    this.hayErrorCodificacion.set(true);
    this.mostrarErrorCodificacion.set(true);

    this.mostrarAlertaError(
      `El archivo CSV está en formato ANSI. Debe estar en UTF-8, conviértelo a UTF-8 e intenta de nuevo.\n(Detectado: ANSI)`,
    );
  }

  private manejarErrorBOM(): void {
    this.codificacionValida.set(false);
    this.hayErrorCodificacion.set(true);
    this.mostrarErrorCodificacion.set(true);

    this.mostrarAlertaError(
      `El archivo CSV debe estar en formato UTF-8 sin BOM. Por favor, guárdalo sin BOM e intenta de nuevo.\n(Detectado: UTF-8 con BOM)`,
    );
  }

  private manejarErrorCodificacionNoSoportada(): void {
    this.codificacionValida.set(false);
    this.hayErrorCodificacion.set(true);
    this.mostrarErrorCodificacion.set(true);

    const esISO8859 =
      this.codificacionDetectada().includes('ISO-8859-1') ||
      this.codificacionDetectada().includes('Windows-1252');

    if (esISO8859) {
      this.mostrarAlertaError(
        `El archivo CSV utiliza codificación ISO-8859-1. Para solucionarlo, abre el archivo en Excel o en un editor de texto, guárdalo como CSV con codificación UTF-8 y vuelve a intentarlo.`,
      );
    } else {
      this.mostrarAlertaError(
        `El archivo CSV no está en formato UTF-8. ` +
          `Por favor, guarde el archivo en formato UTF-8. ` +
          `(Codificación detectada: ${this.codificacionDetectada()})`,
      );
    }
  }

  private manejarCSVValido(): void {
    this.codificacionValida.set(true);
    this.hayErrorCodificacion.set(false);
    this.mostrarErrorCodificacion.set(false);

    this.mostrarAlertaInfo(
      'El archivo CSV está en formato UTF-8 y puede ser importado correctamente.',
    );
  }

  private validarOtroFormato(extension: string): void {
    this.codificacionValida.set(true);
    this.hayErrorCodificacion.set(false);
    this.mostrarErrorCodificacion.set(false);
    this.codificacionDetectada.set('Formato no soportado para validación');
    this.mostrarAlertaWarning(
      `El formato ${extension.toUpperCase()} no está en la lista de formatos soportados para validación. ` +
        'Se recomienda usar CSV o Excel.',
    );
  }

  private async detectarCodificacion(file: File): Promise<void> {
    try {
      const buffer = await this.leerPrimerosBytes(file, 1024);
      const bytes = new Uint8Array(buffer);

      this.tieneBOM.set(this.detectarBOM(bytes));
      this.bytesIniciales.set(this.bytesToHex(bytes.slice(0, 16)));
      const encoding = this.detectarEncoding(bytes);
      this.codificacionDetectada.set(encoding);
    } catch (error) {
      this.codificacionDetectada.set('Error al detectar');
      this.codificacionValida.set(false);
      this.hayErrorCodificacion.set(true);
      this.mostrarErrorCodificacion.set(true);
    }
  }

  private leerPrimerosBytes(file: File, size: number): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const blob = file.slice(0, size);
      reader.onload = (e) => {
        if (e.target?.result instanceof ArrayBuffer) {
          resolve(e.target.result);
        } else {
          reject(new Error('No se pudo leer el archivo'));
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsArrayBuffer(blob);
    });
  }

  private detectarBOM(bytes: Uint8Array): boolean {
    if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
      return true;
    }
    if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
      return true;
    }
    if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
      return true;
    }
    return false;
  }

  private detectarEncoding(bytes: Uint8Array): string {
    if (this.tieneBOM()) {
      if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
        return 'UTF-8 CON BOM (NO SOPORTADO)';
      }
      if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
        return 'UTF-16 LE CON BOM (NO SOPORTADO)';
      }
      if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
        return 'UTF-16 BE CON BOM (NO SOPORTADO)';
      }
    }

    try {
      const decoder = new TextDecoder('utf-8', { fatal: true });
      const decoded = decoder.decode(bytes);

      let tieneCaracteresEspeciales = false;
      for (let i = 0; i < Math.min(decoded.length, 500); i++) {
        if (decoded.charCodeAt(i) > 127) {
          tieneCaracteresEspeciales = true;
          break;
        }
      }

      if (!tieneCaracteresEspeciales) {
        return 'ASCII (compatible UTF-8)';
      }

      return 'UTF-8 (sin BOM)';
    } catch (e) {
      // No es UTF-8 válido
    }

    let isASCII = true;
    for (let i = 0; i < Math.min(bytes.length, 100); i++) {
      if (bytes[i] > 127) {
        isASCII = false;
        break;
      }
    }
    if (isASCII) {
      return 'ASCII (compatible UTF-8)';
    }

    try {
      const decoder = new TextDecoder('iso-8859-1', { fatal: true });
      const decoded = decoder.decode(bytes);
      if (decoded && !decoded.includes('\uFFFD')) {
        return 'ISO-8859-1';
      }
    } catch (e) {
      // No es ISO-8859-1 válido
    }

    try {
      const decoder = new TextDecoder('windows-1252', { fatal: true });
      const decoded = decoder.decode(bytes);
      if (decoded && !decoded.includes('\uFFFD')) {
        return 'Windows-1252';
      }
    } catch (e) {
      // No es Windows-1252 válido
    }

    return 'DESCONOCIDA';
  }

  private bytesToHex(bytes: Uint8Array): string {
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
      hex += bytes[i].toString(16).padStart(2, '0').toUpperCase();
      if (i < bytes.length - 1) hex += ' ';
    }
    return hex;
  }

  private mostrarInformacionFormato(): void {
    if (!this.archivoOriginal) return;

    const extension = this.archivoOriginal.name?.split('.').pop()?.toLowerCase() || '';
    const isExcel = extension === 'xlsx' || extension === 'xls';

    let info =
      `Formato: ${extension.toUpperCase()}\n` +
      `Tamaño: ${this.formatearTamaño(this.archivoOriginal.size)}\n` +
      `Nombre: ${this.archivoOriginal.name}\n`;

    if (isExcel) {
      info += `Tipo: Excel (sin validación de encoding)\n` + `Estado: ✅ Válido para importar`;
    } else {
      info +=
        `Codificación: ${this.codificacionDetectada()}\n` +
        `BOM: ${this.tieneBOM() ? 'Sí' : 'No'}\n` +
        `Bytes iniciales: ${this.bytesIniciales()}\n` +
        `Codificación válida (UTF-8): ${this.codificacionValida() ? 'Sí' : 'No'}\n` +
        `Tipo de archivo: ${extension === 'csv' ? 'CSV (validación requerida)' : 'Otro'}`;
    }
  }

  private mostrarAlertaInfo(mensaje: string): void {
    this.alertaTipo.set('info');
    this.alertaTitulo.set('Información');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  private formatearTamaño(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  limpiarArchivo(): void {
    this.archivoSeleccionado.set(null);
    this.archivoOriginal = null;
    this.nombreArchivo.set('');
    this.importResponse.set(null);
    this.importacionCompletada.set(false);
    this.cerrarAlerta();
    this.hayErrorCodificacion.set(false);
    this.codificacionDetectada.set('Desconocida');
    this.bytesIniciales.set('');
    this.tieneBOM.set(false);
    this.codificacionValida.set(true);
    this.mostrarErrorCodificacion.set(false);
    this.cdr.markForCheck();
  }

  async importarArchivo(): Promise<void> {
    if (!this.archivoOriginal) {
      return;
    }

    const extension = this.archivoOriginal.name?.split('.').pop()?.toLowerCase() || '';

    if (extension === 'csv' || extension === 'txt') {
      if (this.tieneBOM()) {
        this.mostrarAlertaError(
          `El archivo CSV debe estar en formato UTF-8 sin BOM. Por favor, guárdalo sin BOM e intenta de nuevo.\n(Detectado: UTF-8 con BOM)`,
        );
        return;
      }

      if (this.codificacionDetectada() === 'ASCII (compatible UTF-8)') {
        this.mostrarAlertaError(
          `El archivo está en formato ANSI. Debe estar en UTF-8, conviértelo a UTF-8 e intenta de nuevo.\n(Detectado: ANSI)`,
        );
        return;
      }
      if (this.codificacionDetectada() !== 'UTF-8 (sin BOM)') {
        const esISO8859 =
          this.codificacionDetectada().includes('ISO-8859-1') ||
          this.codificacionDetectada().includes('Windows-1252');

        if (esISO8859) {
          this.mostrarAlertaError(
            `El archivo CSV utiliza codificación ISO-8859-1. Para solucionarlo, abre el archivo en Excel o en un editor de texto, guárdalo como CSV con codificación UTF-8 y vuelve a intentarlo.`,
          );
        } else {
          this.mostrarAlertaError(
            `No se puede importar el archivo porque no está en formato UTF-8 ` +
              `(Codificación detectada: ${this.codificacionDetectada()}). ` +
              'Por favor, guarde el archivo en formato UTF-8 sin BOM y vuelva a intentarlo.',
          );
        }
        return;
      }

      if (!this.codificacionValida()) {
        this.mostrarAlertaError(
          `El archivo no es UTF-8 sin BOM válido\n\n` +
            'Asegúrate de que el archivo:\n' +
            '1. Esté en formato UTF-8 sin BOM\n' +
            '2. Tenga los caracteres especiales correctamente codificados',
        );
        return;
      }
    }

    if (!this.puedeImportar()) {
      return;
    }

    this.importando.set(true);
    this.cerrarAlerta();
    this.archivoPendienteImportar = null;

    this.dependenciaService
      .bulkUpload(this.archivoOriginal, this.endpoint, this.fieldName)
      .pipe(
        finalize(() => {
          this.importando.set(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.procesarRespuesta(response);
        },
        error: (error: any) => {
          this.manejarError(error);
        },
      });
  }

  private procesarRespuesta(response: any): void {
    const normalizedResponse = this.normalizarRespuesta(response);

    this.importResponse.set(normalizedResponse);
    this.importacionCompletada.set(true);

    const s = normalizedResponse.summary;

    let mensaje = `Total procesado: ${s.total}, Creados: ${s.created}, Actualizados: ${s.updated}, Omitidos: ${s.skipped}, Inválidos: ${s.invalid}`;

    if (s.failedValidations !== undefined && s.failedValidations !== null) {
      mensaje += `, Validaciones fallidas: ${s.failedValidations}`;
    }

    mensaje += '.';

    if (this.hayErrores()) {
      this.mostrarAlertaWarning(`Advertencias. ${mensaje}`);
    } else {
      this.mostrarAlertaExito(`Importación exitosa. ${mensaje}`);
    }

    this.cdr.markForCheck();
  }

  private normalizarRespuesta(response: any): ImportResponse {
    if (response?.summary) {
      return {
        summary: {
          total: response.summary.total || 0,
          created: response.summary.created || 0,
          updated: response.summary.updated || 0,
          skipped: response.summary.skipped || 0,
          invalid: response.summary.invalid || 0,
          failedValidations: response.summary.failedValidations || 0,
        },
        errors: (response.errors || []).map((err: any) => {
          if (typeof err === 'string') {
            return {
              line: 0,
              field: '',
              rule: '',
              message: err,
              receivedValue: null,
            };
          }
          return err;
        }),
        jobId: response.jobId || null,
      };
    }

    const creadas = response?.creadas?.length ?? 0;
    const erroresLegacy = response?.errores?.length ?? 0;
    const total = creadas + erroresLegacy;

    return {
      summary: {
        total,
        created: creadas,
        updated: response?.actualizadas?.length ?? 0,
        skipped: response?.omitidas?.length ?? 0,
        invalid: erroresLegacy,
        failedValidations: response?.failedValidations || 0,
      },
      errors: (response?.errores || []).map((err: any, index: number) => {
        if (typeof err === 'string') {
          return {
            line: index + 2,
            field: '',
            rule: '',
            message: err,
            receivedValue: null,
          };
        }
        return {
          line: err.line || index + 2,
          field: err.field || '',
          rule: err.rule || '',
          message: err.message || err,
          receivedValue: err.receivedValue || null,
        };
      }),
      jobId: null,
    };
  }

  private manejarError(error: any): void {
    const status = error?.status || error?.error?.status;
    const mensajeError = error?.error?.message || error?.message || '';
    const errors = error?.error?.errors || [];

    const esErrorCodificacion =
      status === 400 &&
      (mensajeError?.toLowerCase().includes('encoding') ||
        mensajeError?.toLowerCase().includes('utf-8') ||
        mensajeError?.toLowerCase().includes('codificacion') ||
        mensajeError?.toLowerCase().includes('codificada') ||
        errors.some(
          (e: string) =>
            e?.toLowerCase().includes('utf-8') ||
            e?.toLowerCase().includes('codificad') ||
            e?.toLowerCase().includes('encoding'),
        ));

    if (esErrorCodificacion) {
      this.hayErrorCodificacion.set(true);
      this.codificacionValida.set(false);

      const esASCII = mensajeError?.toLowerCase().includes('ascii');
      const esISO8859 =
        mensajeError?.toLowerCase().includes('iso-8859-1') ||
        mensajeError?.toLowerCase().includes('windows-1252') ||
        mensajeError?.toLowerCase().includes('ansi');

      let mensaje: string;
      if (esASCII) {
        mensaje =
          'El archivo está en formato ANSI. El sistema requiere UTF-8 sin BOM para manejar caracteres especiales como acentos y eñes.';
      } else if (esISO8859) {
        mensaje =
          'El archivo CSV utiliza codificación ISO-8859-1. Para solucionarlo, abre el archivo en Excel o en un editor de texto, guárdalo como CSV con codificación UTF-8 y vuelve a intentarlo.';
      } else if (mensajeError?.toLowerCase().includes('bom')) {
        mensaje =
          'El archivo CSV debe estar en formato UTF-8 sin BOM. Por favor, guárdalo sin BOM e intenta de nuevo.';
      } else {
        mensaje =
          error?.error?.message ||
          error?.message ||
          `El archivo no está codificado en UTF-8 (Codificación detectada: ${this.codificacionDetectada()}). ` +
            'Por favor, guarde el archivo en formato UTF-8 y vuelva a intentarlo.';
      }

      this.mostrarAlertaError(mensaje);
    } else {
      const mensaje = error?.error?.message || error?.message || 'Error al importar el archivo';
      this.mostrarAlertaError(mensaje);
    }

    this.cdr.markForCheck();
  }

  cerrarDialogo(): void {
    const result: ImportResult = {
      success: this.importacionCompletada(),
      response: this.importResponse() ?? undefined,
    };
    this.dialogRef?.close(result);
  }

  cancelar(): void {
    this.dialogRef?.close({ success: false });
  }

  private mostrarAlertaExito(mensaje: string): void {
    this.alertaTipo.set('success');
    this.alertaTitulo.set('Importación Exitosa');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  private mostrarAlertaError(mensaje: string): void {
    this.alertaTipo.set('error');
    this.alertaTitulo.set('Error');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  private mostrarAlertaWarning(mensaje: string): void {
    this.alertaTipo.set('warning');
    this.alertaTitulo.set('Advertencias');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  cerrarAlerta(): void {
    this.mostrarAlerta.set(false);
  }

  getErrorMessage(error: any): string {
    if (!error) return '';

    if (error.message && !error.message.includes('commons.import.error')) {
      return error.message;
    }

    if (error.rule) {
      const mensajePorRegla = this.getMensajePorRegla(error.rule);
      if (mensajePorRegla) {
        return mensajePorRegla;
      }
    }

    if (error.message && error.message.includes('commons.import.error')) {
      return this.traducirCodigoError(error.message);
    }

    return error.message || 'Error desconocido';
  }

  private getMensajePorRegla(rule: string): string {
    if (!rule) return '';

    const ruleUpper = rule.toUpperCase();

    const mapaReglas: { [key: string]: string } = {
      IMPORT_REQUIRED_FIELD: 'Campo requerido',
      IMPORT_TYPE_MISMATCH: 'Tipo de dato inválido',
      IMPORT_VALIDATION: 'Error de validación',
      IMPORT_DUPLICATE: 'Registro duplicado',
      IMPORT_FORMAT_ERROR: 'Formato inválido',
      IMPORT_LENGTH_ERROR: 'Longitud excedida',
    };

    for (const [key, value] of Object.entries(mapaReglas)) {
      if (ruleUpper.includes(key) || key.includes(ruleUpper)) {
        return value;
      }
    }

    return '';
  }

  private traducirCodigoError(codigo: string): string {
    const traducciones: { [key: string]: string } = {
      'commons.import.error.campo.requerido': 'Campo requerido',
      'commons.import.error.tipo.invalido': 'Tipo de dato inválido',
      'commons.import.error.formato.invalido': 'Formato inválido',
      'commons.import.error.valor.duplicado': 'Valor duplicado',
      'commons.import.error.longitud.excedida': 'Longitud excedida',
      'commons.import.error.campo.no.existe': 'Campo no existe',
      'commons.import.error.registro.duplicado': 'Registro duplicado',
      'commons.import.error.valor.no.permitido': 'Valor no permitido',
    };

    if (traducciones[codigo]) {
      return traducciones[codigo];
    }

    for (const [key, value] of Object.entries(traducciones)) {
      if (codigo.includes(key) || key.includes(codigo)) {
        return value;
      }
    }

    return codigo;
  }

  getErrorClass(rule: string): string {
    if (!rule) return '';

    const ruleUpper = rule.toUpperCase();

    if (ruleUpper.includes('IMPORT_REQUIRED_FIELD')) return 'error-required';
    if (ruleUpper.includes('IMPORT_TYPE_MISMATCH')) return 'error-type';
    if (ruleUpper.includes('IMPORT_VALIDATION')) return 'error-validation';
    if (ruleUpper.includes('IMPORT_DUPLICATE')) return 'error-duplicate';

    return 'error-default';
  }

  descargarReporteErrores(): void {
    if (!this.errors() || this.errors().length === 0) {
      return;
    }

    let txtContent = '';

    txtContent += '#\tLínea\tCampo\tMensaje\tValor Recibido\n';

    this.errors().forEach((error, index) => {
      const mensajeFinal = this.getErrorMessage(error);

      txtContent += `${index + 1}\t${error.line || 'N/A'}\t${error.field || '-'}\t${mensajeFinal}\t${error.receivedValue || '(vacío)'}\n`;
    });

    const blob = new Blob([txtContent], {
      type: 'text/plain;charset=utf-8;',
    });

    const fileName = `errores-importacion-${new Date().toISOString().slice(0, 10)}.txt`;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private async validarCodificacionUTF8(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (event: ProgressEvent<FileReader>) => {
        try {
          const result = event.target?.result;

          if (result instanceof ArrayBuffer) {
            const bytes = new Uint8Array(result);

            if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
              resolve(false);
              return;
            }

            try {
              const decoder = new TextDecoder('utf-8', { fatal: true });
              const decoded = decoder.decode(bytes);

              let tieneNoASCII = false;
              for (let i = 0; i < Math.min(decoded.length, 1000); i++) {
                if (decoded.charCodeAt(i) > 127) {
                  tieneNoASCII = true;
                  break;
                }
              }

              resolve(tieneNoASCII);
            } catch (e) {
              resolve(false);
            }
          } else if (typeof result === 'string') {
            if (result.includes('\uFFFD')) {
              resolve(false);
              return;
            }

            try {
              const encoder = new TextEncoder();
              const decoder = new TextDecoder('utf-8', { fatal: true });
              const encoded = encoder.encode(result);
              const decoded = decoder.decode(encoded);

              let tieneNoASCII = false;
              for (let i = 0; i < Math.min(decoded.length, 1000); i++) {
                if (decoded.charCodeAt(i) > 127) {
                  tieneNoASCII = true;
                  break;
                }
              }

              resolve(decoded === result && tieneNoASCII);
            } catch (e) {
              resolve(false);
            }
          } else {
            resolve(false);
          }
        } catch (e) {
          resolve(false);
        }
      };

      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file);
    });
  }
}
